import { Broadcaster } from '@core/app/scripts/services/broadcaster';
import { DisplayMessage } from '@core/app/scripts/services/display-message';
import { Endpoint } from '@core/app/scripts/services/endpoint';
import { JsonService } from '@core/app/scripts/services/json-service';
import { Mask } from '@core/app/scripts/services/mask';
import { Sentry } from '@shared/app/scripts/services/sentry';
import { SessionManager } from '@shared/app/scripts/services/session-manager';

export class HandleData {
  _timeout = 120000;
  SESSION_COOKIE = 'sid';

  constructor(
    private $http: ng.IHttpService,
    private $log: ng.ILogService,
    private $q: ng.IQService,
    private $timeout: ng.ITimeoutService,
    private $translate: ng.translate.ITranslateService,
    private broadcaster: Broadcaster,
    private displayMessage: DisplayMessage,
    private endpoint: Endpoint,
    private ipCookie,
    private jsonService: JsonService,
    private mask: Mask,
    private sentry: Sentry,
    private sessionManager: SessionManager,
    private $location: ng.ILocationService,
  ) {
    //the resource provider interacting with the FP and Spynl API Backend
    this.spynl = this.endpoint.getSpynlEndpoint();
  }

  buffer = {};
  sid = null;
  spynl = '';

  get = (url, success, error, options, req_id) => {
    if (!success) {
      success = null;
    }
    if (!options) {
      options = null;
    }

    //Create buffer object
    const buffer = {
      success: success,
      error: error,
      method: 'urlGet',
      url: url,
      options: options,
    };

    if (!error) {
      error = this.error;
    }

    //Set defaults
    const payload = {
      url: url,
      method: 'GET',
      datatype: 'json',
      cache: false,
      timeout: this._timeout,
    };
    angular.extend(payload, options);

    //Put the request in the buffer
    req_id = req_id ? req_id : this.saveBuffer(buffer);

    //Use internal promise handleData for buffering
    const promiseFunc = (resp, status) => {
      this.promise(resp, status, req_id);
    };

    let errorFunc;

    if (options && options.customError) {
      errorFunc = error;
    } else {
      errorFunc = (error, status) => {
        this.error(error, status, req_id);
      };
    }

    if (payload.datatype == 'jsonp') {
      return this._jsonp(payload, promiseFunc, errorFunc);
    } else {
      return this._http(payload, promiseFunc, errorFunc);
    }
  };

  _http = (payload, successFunction, errorFunction) => {
    payload.withCredentials = angular.isDefined(payload.withCredentials)
      ? payload.withCredentials
      : true;

    if (!angular.isFunction(successFunction)) {
      successFunction = angular.noop;
    }

    if (!angular.isFunction(errorFunction)) {
      errorFunction = angular.noop;
    }

    const httpPromise = this.$http(payload);

    httpPromise.then(
      (response) => successFunction(response.data),
      errorFunction,
    );

    return httpPromise;
  };

  _jsonp = (payload, successFunction, errorFunction) => {
    payload.url = payload.url + '?callback=JSON_CALLBACK';
    payload.withCredentials = angular.isDefined(payload.withCredentials)
      ? payload.withCredentials
      : true;

    this.$http
      .jsonp(payload.url)
      .then((response) => successFunction(response.data), errorFunction);
  };

  _toJSON = (data, headers) => {
    if (typeof data === 'string') {
      try {
        data = JSON.parse(data);
      } catch (err) {
        this.$log.error('[HandleData] JSON Parse Error!', err);
        data = {};
      }
    }
    return data;
  };

  _fromJSON = (data, headers) => {
    // @ts-ignore
    return this.jsonService.toJson(data);
  };

  _handleRequest = (
    resource,
    method,
    filter,
    data,
    jobs,
    success,
    error,
    options,
    req_id,
  ) => {
    let errorFunc, promiseFunc, requestBody, url, payload, buffer;

    success = success ? success : null;
    options = options ? options : null;
    data = data ? data : null;
    filter = filter ? filter : null;
    jobs = jobs ? jobs : null;
    error = error ? error : this.error;

    //Create the buffer if a req_id is not provided
    buffer = {
      success: success,
      error: error,
      filter: filter,
      data: data,
      jobs: jobs,
      resource: resource,
      method: method,
      options: options,
    };
    req_id = req_id ? req_id : this.saveBuffer(buffer);

    //Use internal promise handleData for buffering
    promiseFunc = (resp, status) => {
      this.promise(resp, status, req_id);
    };

    if (options && options.customError) {
      errorFunc = error;
    } else {
      errorFunc = (error, status) => {
        this.error(error, status, req_id);
      };
    }

    requestBody = {};
    if (data) {
      requestBody.data = data;
    }
    if (jobs) {
      requestBody.jobs = jobs;
    }

    if (filter) {
      requestBody.filter = filter;
    }

    angular.extend(requestBody, options);

    //Create the URL
    url = this.spynl + '/' + resource + '/' + method;
    //Set defaults
    payload = {
      url: url,
      method: 'POST',
      data: requestBody,
      datatype: 'json',
      cache: false,
      timeout: this._timeout,
      transformResponse: this._toJSON,
      transformRequest: this._fromJSON,
    };

    return this._http(payload, promiseFunc, errorFunc);
  };

  /*
   * A simple method to handle creating query strings
   * @param {String} url the base url
   * @param {Object|String} params the object of params or query string
   */
  _urlBuilder = (url, params) => {
    if (!url) {
      return '';
    }
    if (!params) {
      return url;
    }
    let qs = '';
    let pairs, length;
    //Only recurse through objects
    if (angular.isObject(params)) {
      // @ts-ignore
      pairs = _.toPairs(params);
      length = pairs.length;
      //Add key value pairs together
      for (const i in pairs) {
        qs += pairs[i][0] + '=' + encodeURIComponent(pairs[i][1]);
        //Append & in between each property except for the last
        // @ts-ignore
        if (i != length - 1) {
          qs += '&';
        }
      }
    } else if (angular.isString(params)) {
      qs = params;
    }
    url += '?' + qs;
    return url;
  };

  addData = (urlparams, data, success, error, options, req_id) => {
    if (!urlparams || !urlparams.resource) {
      this.$log.error(
        '[HandleData] addData: resource is a required parameter!',
      );
      return;
    }
    return this._handleRequest(
      urlparams.resource,
      'add',
      null,
      data,
      null,
      success,
      error,
      options,
      req_id,
    );
  };

  updateData = (urlparams, filter, data, success, error, options, req_id) => {
    if (!urlparams || !urlparams.resource) {
      this.$log.error(
        '[HandleData] updateData: resource is a required parameter!',
      );
      return;
    }
    return this._handleRequest(
      urlparams.resource,
      'edit',
      filter,
      data,
      null,
      success,
      error,
      options,
      req_id,
    );
  };

  saveData = (urlparams, data, success, error, options, req_id) => {
    if (!urlparams || !urlparams.resource) {
      this.$log.error(
        '[HandleData] saveData: resource is a required parameter!',
      );
      return;
    }
    return this._handleRequest(
      urlparams.resource,
      'save',
      null,
      data,
      null,
      success,
      error,
      options,
      req_id,
    );
  };

  getData = (urlparams, filter, success, error, options, req_id) => {
    if (!urlparams || !urlparams.resource) {
      this.$log.error(
        '[HandleData] getData: resource is a required parameter!',
      );
      return;
    }

    return this._handleRequest(
      urlparams.resource,
      'get',
      filter,
      null,
      null,
      success,
      error,
      options,
      req_id,
    );
  };

  time = (success, error) => {
    const payload = {
      url: this.spynl + '/time',
      method: 'POST',
      data: {},
      datatype: 'json',
      cache: false,
      timeout: this._timeout,
    };
    return this._http(payload, success, error);
  };

  getSID = () => {
    const sidParam = this.$location.search().sid;
    const cookieSid = this.ipCookie(this.SESSION_COOKIE);
    const sid = sidParam || cookieSid;
    return sid;
  };

  /**
   * Standard promise function
   * @param {Object} the response from the server
   * @param {String} req_id the random req_id generated by the request buffer
   */
  promise = (resp, status, req_id) => {
    const resource = this.buffer[req_id].resource;
    const method = this.buffer[req_id].method;
    if (status != 206) {
      //Send a generic broadcast event that the retry was aborted
      this.broadcaster.send('appHandleDataSuccess', resource);
      //Get the buffered promise function
      const success = this.buffer[req_id].success;
      if (success) {
        success(resp);
      } else {
        //If no promise function, display default message
        if (
          method === 'save' ||
          method === 'add' ||
          method === 'edit' ||
          method === 'multi-edit'
        ) {
          const message = this.$translate.instant('DataSaveSuccess');
          this.displayMessage.message({
            message: message,
            type: 'info',
            callback: null,
            mini: true,
          });
        }
      }
      this.removeBuffer(req_id);
    } else {
      this.error({}, status, req_id);
    }
  };

  /**
   * Standard error response
   * @param {Object, String} error error response from the server
   * @param {Number} errorStatus HTTP status response from the server
   * @param {String} req_id the random req_id generated by the request buffer
   */
  error = (error, errorStatus, req_id) => {
    let status = 0;
    const errorStatuses = [400, 401, 403, 404, 415, 500, 502, 503, 504];
    let message = '';
    this.mask.hide();

    if (!this.buffer[req_id]) {
      return;
    }

    if (!error) {
      error = {};
    }
    if (angular.isNumber(errorStatus)) {
      status = errorStatus;
    } else if (angular.isNumber(error.status)) {
      status = error.status;
    }

    const errorCallback = this.buffer[req_id].error,
      successCallback = this.buffer[req_id].success;

    if (status === 0) {
      message = this.$translate.instant('Offline');
    } else if (error.type === 'DuplicateTransaction') {
      // quietly handle the error it on the POS side
      this.removeBuffer(req_id);

      this.sentry.captureException(new Error('DuplicateTransaction'));

      if (angular.isFunction(successCallback)) {
        // special value to make pos service believe all went fine
        successCallback({ data: [{}] });
      }
      return;
      // @ts-ignore
    } else if (_.includes(errorStatuses, errorStatus)) {
      this.logError(req_id, status);
      this.removeBuffer(req_id);
      if (angular.isFunction(errorCallback)) {
        errorCallback(status);
      }
      return;
    } else if (error.message == 'session expired') {
      //Used by FoxPro API when session expired
      this.logError(req_id, error.message);
      this.removeBuffer(req_id);
      if (angular.isFunction(errorCallback)) {
        errorCallback(error.message);
      }
      return;
    } else {
      message = this.$translate.instant('DataSaveError');
    }

    //If there is an error, broadcast the error if the user wants to retry the request.
    //Caught by the DataSaveError listener
    const retry = (resp) => {
      const dataTransaction = this.buffer[req_id];
      let resource;

      if (dataTransaction.timer) {
        this.$timeout.cancel(dataTransaction.timer);
        delete dataTransaction.timer;
      }
      //Get the buffered promise function
      resp = !resp ? 'yes' : resp;
      if (resp == 'yes') {
        dataTransaction.retry++;
        //Use internal promise handleData for buffering
        const promiseFunc = (resp, status) => {
          this.promise(resp, status, req_id);
        };
        const errorFunc = (error, status) => {
          this.error(error, status, req_id);
        };
        const data = dataTransaction.data;
        const filter = dataTransaction.filter;
        const url = dataTransaction.url;
        resource = dataTransaction.resource;
        const method = dataTransaction.method;
        const options = dataTransaction.options;
        if (method == 'edit') {
          this.updateData(
            {
              resource: resource,
            },
            filter,
            data,
            promiseFunc,
            errorFunc,
            options,
            req_id,
          );
        } else if (method == 'add') {
          this.addData(
            {
              resource: resource,
            },
            data,
            promiseFunc,
            errorFunc,
            options,
            req_id,
          );
        } else if (method == 'get') {
          this.getData(
            {
              resource: resource,
            },
            filter,
            promiseFunc,
            errorFunc,
            options,
            req_id,
          );
        } else if (method == 'save') {
          this.saveData(
            {
              resource: resource,
            },
            data,
            promiseFunc,
            errorFunc,
            options,
            req_id,
          );
        } else if (method == 'urlGet') {
          this.get(url, promiseFunc, errorFunc, options, req_id);
        }
      }
      if (resp == 'no') {
        resource = dataTransaction.resource;
        const errorCallback = dataTransaction.error;
        this.removeBuffer(req_id);
        //Call the original error handler if there is one
        if (errorCallback && typeof errorCallback === 'function') {
          errorCallback();
        }
        //Send a generic broadcast event that the retry was aborted
        this.broadcaster.send('appHandleDataError', resource);
      }
    };
    // Auto retry
    let time;
    let attempt = this.buffer[req_id].retry;
    // SWUI-1455 stop retries from growing above 1h and the potential for NaN retries
    attempt = attempt > 20 ? 1 : attempt;

    if (attempt < 2) {
      time = 2000;
    } else {
      time = 1000 * Math.pow(2, attempt - 1);
    }
    const resource =
      this.buffer[req_id].resource !== undefined
        ? this.buffer[req_id].resource
        : this.buffer[req_id].url;
    this.$log.log(
      '[handleData] Auto Retry for ' +
        resource +
        ' buffer: ' +
        req_id +
        ' Retry: ' +
        this.buffer[req_id].retry,
    );
    if (time < 3000) {
      this.displayMessage.message({
        message: this.$translate.instant('StillWorking'),
        type: 'info',
        mini: true,
      });
      this.$timeout(retry, time, true);
    } else {
      const timer = this.$timeout(retry, time, true);
      this.buffer[req_id].timer = timer;
      //store this instance of the retry function
      this.buffer[req_id].retryFunction = retry;

      this.displayMessage.message({
        message: this.$translate.instant('Retrying in') + ` ${time / 1000}s`,
        time: time,
        type: 'info',
        mini: true,
      });
    }
  };

  /**
   * Error logging function. Logs standard server errors to Sentry and displays message to client.
   * @param {String} req_id the random request id generated by the request
   * @param {Number, String} status the status reported by the server
   */
  logError = (req_id, status) => {
    let error_string = 'General Error! ',
      callback = null,
      type = 'alert',
      message = this.$translate.instant('ThereWasAnError'),
      resource,
      url,
      string_filter,
      string_data;

    if (status == 500) {
      error_string = '500 Server Error!';
      message = this.$translate.instant('ServerError');
    } else if (status == 401) {
      error_string = '401 User Not Authorised!';
      message = this.$translate.instant('NotAuthorisedForResource');
    } else if (status == 400) {
      error_string = '400 Bad Request!';
      message = this.$translate.instant('BadRequest');
    } else if (status == 404) {
      error_string = '404 Not Found!';
      message = this.$translate.instant('NotFound');
    } else if (status == 'session expired') {
      error_string = 'FoxPro Session Expired!';
      message = this.$translate.instant('SessionExpired');
      type = 'errorprompt';
      callback = () => {
        this.sessionManager.logout();
      };
    }

    resource = this.buffer[req_id].resource;
    url = this.buffer[req_id].url;

    string_filter = this.buffer[req_id].filter
      ? this.buffer[req_id].filter
      : 'No filter';
    string_data = this.buffer[req_id].data
      ? this.buffer[req_id].data
      : 'No filter';

    // Generic error broadcaster
    this.broadcaster.send('appHandleDataError', resource);

    // Log errors to console and to Sentry
    error_string += ' Resource: ' + resource;

    this.$log.error(error_string, string_filter, string_data);

    this.sentry.captureException(new Error('Handle data: ' + error_string));

    this.displayMessage.message({
      message: message,
      callback: callback,
      type: type,
    });
  };

  /** Saves requests into a buffer for processing when there is no connection
   * @param {Object} buffer - an object containing any of the following paramters
   * @param {Function} buffer.success - promise function
   * @param {Object} buffer.filter - the database query
   * @param {Object} buffer.data - data to post
   * @param {String} buffer.resource - data resource (not used with generic post/gets)
   * @param {String} buffer.method - post, get, add, edit, remove, save
   * @param {String} buffer.url - URL to get/post to when doing a standard GET/POST request
   * @param {Object} buffer.options - any additional AJAX options
   */
  saveBuffer = (buffer) => {
    let guid = this.generateRandom();
    while (this.buffer[guid]) {
      guid = this.generateRandom();
    }
    this.buffer[guid] = buffer;
    this.buffer[guid].retry = 0;
    return guid;
  };

  removeBuffer = (guid) => {
    try {
      if (this.buffer[guid].timer) {
        this.$timeout.cancel(this.buffer[guid].timer);
      }
      delete this.buffer[guid];
    } catch (err) {
      this.$log.error('[handleData] Error deleting buffer: ' + guid);
    }
  };

  generateRandom = () => {
    return Math.floor((1 + Math.random()) * 0x10000)
      .toString(16)
      .substring(1);
  };

  /**
   * @ngdoc
   * @name v6CoreModule.handleData#getNewPosInstanceId
   * @methodOf v6CoreModule.handleData
   * @description
   * Query the spynl for the new pos instance id.
   * @return {promise} resolves with the new pos instance id; or rejects with the error
   */
  getNewPosInstanceId = () => {
    // @ts-ignore
    return this._handleRequest('devices', 'new-pos-instance-id').then(
      (response) => {
        if (
          // @ts-ignore
          angular.isObject(response.data) &&
          // @ts-ignore
          angular.isNumber(response.data.data)
        ) {
          // @ts-ignore
          return response.data.data;
        }

        return this.$q.reject();
      },
    );
  };
}

/**
 * @ngdoc service
 * @name v6CoreModule.handleData
 * @description
 * The data transport layer to communicate with all APIs
 */
angular
  .module('v6CoreModule')
  .service(
    'handleData',
    (
      $http: ng.IHttpService,
      $log: ng.ILogService,
      $q: ng.IQService,
      $timeout: ng.ITimeoutService,
      broadcaster: Broadcaster,
      displayMessage: DisplayMessage,
      endpoint: Endpoint,
      ipCookie,
      jsonService: JsonService,
      mask: Mask,
      sentry: Sentry,
      sessionManager: SessionManager,
      translate: ng.translate.ITranslateService,
      $location: ng.ILocationService,
    ) => {
      return new HandleData(
        $http,
        $log,
        $q,
        $timeout,
        translate,
        broadcaster,
        displayMessage,
        endpoint,
        ipCookie,
        jsonService,
        mask,
        sentry,
        sessionManager,
        $location,
      );
    },
  );
